import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ToolsService, GetMaterialsForSearchResultDto, CreateMaterialRequest, UpdateMaterialRequest } from '../../services/tools.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AddMaterialDialogComponent, AddMaterialResult } from './add-material-dialog/add-material-dialog.component';
import { EditMaterialDialogComponent, EditMaterialData, EditMaterialResult } from './edit-material-dialog/edit-material-dialog.component';

interface MaterialRow {
  materialId: number;
  name: string;
  functionName: string;
  sg: number | null;
  sackWeightLb: number | null;
  addAfterStirring: boolean;
  isActive: boolean;
  noFluidLossWithFoamers: boolean;
  materialTypeId: number;
  materialTypeName: string;
}

enum MaterialTypes {
  Dry = 1,
  Liquid = 2,
  Cement = 3,
  LiquidBase = 4,
  Pozolan = 5,
  LiquidAdditive = 6,
  SpacerDryBase = 7,
  SpacerDryFractionBase = 8,
  SpacerLiquidBase = 9,
  SpacerLiquidFractionBase = 10,
  SpacerDryAdditive = 11,
  SpacerLiquidAdditive = 12
}

interface MaterialTypeOption {
  label: string;
  value: MaterialTypes;
}

const MATERIAL_TYPE_OPTIONS: MaterialTypeOption[] = [
  { label: 'Dry Additive', value: MaterialTypes.Dry },
  { label: 'Liquid Additive', value: MaterialTypes.Liquid },
  { label: 'Cement', value: MaterialTypes.Cement },
  { label: 'Liquid Base', value: MaterialTypes.LiquidBase },
  { label: 'Poz', value: MaterialTypes.Pozolan },
  { label: 'Spacer Dry Base', value: MaterialTypes.SpacerDryBase },
  { label: 'Fraction Spacer Dry Base', value: MaterialTypes.SpacerDryFractionBase },
  { label: 'Spacer Liquid Base', value: MaterialTypes.SpacerLiquidBase },
  { label: 'Fraction Spacer Liquid Base', value: MaterialTypes.SpacerLiquidFractionBase }
];

function getMaterialTypeLabel(enumValue: MaterialTypes): string {
  const option = MATERIAL_TYPE_OPTIONS.find(opt => opt.value === enumValue);
  return option ? option.label : 'Unknown';
}

function getMaterialTypeValue(label: string): MaterialTypes | undefined {
  const option = MATERIAL_TYPE_OPTIONS.find(opt => opt.label === label);
  return option?.value;
}

@Component({
  selector: 'app-manage-materials',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatDialogModule],
  templateUrl: './manage-materials.component.html',
  styleUrl: './manage-materials.component.css',
})
export class ManageMaterialsComponent implements OnInit {
  nameOrFunctionFilter = '';
  selectedType: MaterialTypes = MaterialTypes.Dry;

  readonly materialTypeOptions = MATERIAL_TYPE_OPTIONS;

  materials: MaterialRow[] = [];
  filteredMaterials: MaterialRow[] = [];

  isSearching = false;
  isSaving = false;

  constructor(
    private toolsService: ToolsService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<ManageMaterialsComponent>
  ) {}

  ngOnInit(): void {
    
  }

  close(): void {
    this.dialogRef.close();
  }

  compareById(a: number, b: number): boolean {
    return a === b;
  }

  onSearchClick(): void {
    const searchTerm = this.nameOrFunctionFilter;

    this.isSearching = true;

    const materialType = this.selectedType;

    this.toolsService.searchMaterials({
      searchTerm: searchTerm,
      materialType: materialType  
    }).subscribe({
      next: (results) => {
        this.materials = results.map(dto => this.mapToMaterialRow(dto));
        this.filteredMaterials = this.materials;
        this.isSearching = false;
      },
      error: (err: HttpErrorResponse) => {
        this.isSearching = false;
        this.materials = [];
        this.filteredMaterials = [];
      }
    });
  }

  private mapToMaterialRow(dto: GetMaterialsForSearchResultDto): MaterialRow {
    return {
      materialId: dto.materialId,
      name: dto.materialName || '',
      functionName: dto.functionName || '',
      sg: dto.specificGravity,
      sackWeightLb: dto.sackWeightLb,
      addAfterStirring: dto.addAfterStirring,
      isActive: dto.isActive ?? true,
      noFluidLossWithFoamers: dto.isFluidLossTestWithFoamNeeded ?? false,
      materialTypeId: dto.materialTypeID,
      materialTypeName: this.getMaterialTypeName(dto.materialTypeID)
    };
  }

  private getMaterialTypeName(typeId: number): string {
    const typeOption = MATERIAL_TYPE_OPTIONS.find(option => option.value === typeId);
    return typeOption ? typeOption.label : 'Unknown';
  }

  AddMaterialCommand(): void {
    const dialogRef = this.dialog.open(AddMaterialDialogComponent, {
      width: '750px',
      maxHeight: '90vh',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: AddMaterialResult | undefined) => {
      if (!result) {
        return;
      }

      this.isSaving = true;

      const request: CreateMaterialRequest = {
        name: result.name,
        functionName: result.functionName || '',
        materialTypes: result.materialType,
        specificGravity: result.specificGravity,
        sackWeight: result.sackWeightLb,
        addAfterStirring: result.addAfterStirring,
        isActive: result.isActive,
        isFluidLossTestWithFoamNeeded: result.doNotRunFluidLossTestWithFoamers
      };

      this.toolsService.createMaterial(request).subscribe({
        next: (created) => {
          this.isSaving = false;
          
          if (this.nameOrFunctionFilter.length > 0) {
            this.onSearchClick();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving = false;
        }
      });
    });
  }

  onMaterialDoubleClick(material: MaterialRow): void {
    const editData: EditMaterialData = {
      materialId: material.materialId,
      name: material.name,
      functionName: material.functionName || '',
      specificGravity: material.sg,
      sackWeightLb: material.sackWeightLb,
      materialType: material.materialTypeId,
      addAfterStirring: material.addAfterStirring,
      isActive: material.isActive,
      doNotRunFluidLossTestWithFoamers: material.noFluidLossWithFoamers
    };

    const dialogRef = this.dialog.open(EditMaterialDialogComponent, {
      width: '750px',
      maxHeight: '90vh',
      disableClose: true,
      data: editData
    });

    dialogRef.afterClosed().subscribe((result: EditMaterialResult | undefined) => {
      if (!result) {
        return;
      }

      this.isSaving = true;

      const updateRequest: UpdateMaterialRequest = {
        name: result.name,
        functionName: result.functionName || '',
        materialTypes: result.materialType,
        specificGravity: result.specificGravity,
        sackWeight: result.sackWeightLb,
        addAfterStirring: result.addAfterStirring,
        isActive: result.isActive,
        isFluidLossTestWithFoamNeeded: result.doNotRunFluidLossTestWithFoamers
      };

      this.toolsService.updateMaterial(result.materialId, updateRequest).subscribe({
        next: (updated) => {
          this.isSaving = false;
          
          if (this.nameOrFunctionFilter.length > 0) {
            this.onSearchClick();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.isSaving = false;
        }
      });
    });
  }
}

